import { Stack, Typography, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { type FC, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { ReminderAddDrawer } from "web_myplann/Shared/Components/ReminderAddDrawer";
import { ReminderRow } from "web_myplann/Shared/Components/ReminderRow";
import { useAutoResetState } from "web_myplann/Shared/Hooks/useAutoResetState";
import {
	REMINDER_HIGHLIGHT_DURATION_MS,
	type ReminderEntry,
} from "web_myplann/Shared/Utils/reminder";

type Props = {
	reminders?: ReminderEntry[];
	onChange?: (reminders: ReminderEntry[]) => void;
	initialReminders?: ReminderEntry[];
	expiryDate?: Date | null;
	title?: string;
};

export const ReminderSection: FC<Props> = ({
	reminders,
	onChange,
	initialReminders,
	expiryDate,
	title,
}) => {
	const { palette } = useTheme();
	const { t } = useResource();
	const [internal, setInternal] = useState<ReminderEntry[]>(
		initialReminders ?? [],
	);
	const [open, setOpen] = useState(false);
	const [highlightedId, highlight] = useAutoResetState<string | null>(
		null,
		REMINDER_HIGHLIGHT_DURATION_MS,
	);

	const list = reminders ?? internal;
	const apply = (next: ReminderEntry[]) =>
		onChange ? onChange(next) : setInternal(next);

	const add = (draft: Omit<ReminderEntry, "id">) =>
		apply(
			[
				...list,
				{
					...draft,
					id: crypto.randomUUID?.() ?? Math.random().toString(36).substring(2),
				},
			].sort((a, b) => a.date.getTime() - b.date.getTime()),
		);

	const remove = (id: string) => apply(list.filter((r) => r.id !== id));

	const openExisting = (reminder: ReminderEntry) => {
		setOpen(false);
		highlight(reminder.id);
	};

	return (
		<>
			<Stack mt={0.8}>
				<Typography
					variant="overline"
					color={palette.grey[500]}
					px={1.6}
					pt={2.4}
					pb={0.8}
				>
					{title ?? t(Resources.Reminder.title)}
				</Typography>

				<FlatRowGroup>
					{list.map((reminder) => (
						<ReminderRow
							key={reminder.id}
							reminder={reminder}
							highlighted={reminder.id === highlightedId}
							onRemove={() => remove(reminder.id)}
						/>
					))}

					<Stack
						direction="row"
						alignItems="center"
						gap={1.4}
						px={1.6}
						py={1.4}
						onClick={() => setOpen(true)}
						sx={{
							cursor: "pointer",
							"&:active": { bgcolor: palette.grey[50] },
						}}
					>
						<Stack
							width={32}
							height={32}
							alignItems="center"
							justifyContent="center"
							flexShrink={0}
						>
							<Plus size={16} color={palette.blue[600]} />
						</Stack>
						<Typography variant="bodyMedium" color={palette.blue[600]}>
							{t(Resources.Reminder.add)}
						</Typography>
					</Stack>
				</FlatRowGroup>
			</Stack>

			<ReminderAddDrawer
				open={open}
				onClose={() => setOpen(false)}
				expiryDate={expiryDate}
				existingReminders={list}
				onOpenExisting={openExisting}
				onAdd={add}
			/>
		</>
	);
};
